import { QRCodeFormSchema } from '@app/domain/qr-code/api/qr-code.form.schema.api';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { QRCodeSocialNetworks } from '../../QRCodeSocialNetworks';
import { GS1ProductInfo } from './GS1ProductInfo';
import { QRCodeDesignSection } from '../../QRCodeDesignSection';
import { TranslationKey } from '@app/locales/defaultTranslation';
import { QRCodeStepper } from '../../QRCodeStepper';
import { QRCodeLinksEditor } from '../../QRCodeLinksEditor';
import { QRCodeHeaderEditor } from '../../QRCodeHeaderEditor';
import { useHandler } from '@app/hooks/useHandler.hook';
import { COMMAND_EFFECT_REQUEST } from '../../qr-code-form.constants';

/*
TODO show form errors if any
With current behavior, if form invalid and related property not rendered
user will not see this error anyhow and will be blocked to navigate to other form steps.
Need a universal solution for this.
Maybe errors summary or at least snackbar.
But what if component rendered and focused on error - snackbar will be redundant in this case.
Maybe show snackbar only for null error types - which mean property was not buond to an input yet?
But also value may be not null and still invalid and still not linked to currently visible input
because input may be conditionally rendered in other form steps, for example.
*/

const gs1Steps: TranslationKey[] = ['qr.gs1Input.productInfoTab', 'qr.gs1Input.linksTab', 'qr.gs1Input.designTab'];

export const QRCodeGS1: React.FC<{
  forWidgetMode?: boolean;
  disabledEditInput?: boolean;
  isEditMode?: boolean;
}> = ({ forWidgetMode, disabledEditInput, isEditMode }) => {
  const [activeStep, setActiveStep] = useState(0);
  const {
    trigger,
    formState: { errors },
    watch,
  } = useFormContext<QRCodeFormSchema>();

  const lastCommandEffectRequest = watch('lastCommandEffectRequest');
  useEffect(() => {
    if (!lastCommandEffectRequest) {
      return;
    }
    if (lastCommandEffectRequest === COMMAND_EFFECT_REQUEST.GS1_NEED_TO_ENTER_TITLE) {
      setActiveStep(1);
    }
  }, [lastCommandEffectRequest]);

  const validateAndSetStep = useHandler(async (nextStep: number) => {
    const isGS1Valid = await trigger('gs1.gtin', { shouldFocus: true });
    const isTitleValid = await trigger('gs1.title', { shouldFocus: true });

    if (!isGS1Valid) {
      setActiveStep(0);
      return;
    }

    if (!isTitleValid) {
      setActiveStep(1);
      return;
    }

    setActiveStep(nextStep);
  });

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      validateAndSetStep(activeStep);
    }
  }, [errors, validateAndSetStep, activeStep]);

  return (
    <div className="flex flex-col gap-4">
      <QRCodeStepper steps={gs1Steps} activeStep={activeStep} setActiveStep={validateAndSetStep} />

      {activeStep === 0 && <GS1ProductInfo disabledEditInput={disabledEditInput} isEditMode={isEditMode} />}

      {activeStep === 1 && (
        <>
          <QRCodeHeaderEditor prefix="gs1" forWidgetMode={forWidgetMode} disabledEditInput={disabledEditInput} />
          <QRCodeLinksEditor
            forWidgetMode={forWidgetMode}
            disabledEditInput={disabledEditInput}
            linksFormPath="gs1.customLinks"
          />
          <QRCodeSocialNetworks formPath="gs1.socialLinks" disabledEditInput={disabledEditInput} />
        </>
      )}

      {activeStep === 2 && <QRCodeDesignSection prefix={'gs1'} />}
    </div>
  );
};
